import { useCallback, useMemo } from 'react'
import { useEffect } from 'react'
import { clearLoadingMask } from '../../framework/loadingMask'
import { Button, classNames, Title, Subtitle, InvalidPermissionsSVG } from '@panwds/react-ui'
import { PANWDS_TW3_CLS } from '../../patches/panwds-constants'
// import { AuthenticationContext } from './OidcAuth'
import { logout } from '../../framework/actions'
import { clearStoredExpiredTsgTokens } from '../../framework/tsg'
import { _T } from '../../i18n'
import './FullscreenError.scss'
import type { FC, VFC, ReactNode, ReactElement } from 'react'

const getMessageParam = (): string => {
  const param = new URLSearchParams(window.location.search)
  return param.get('message') || ''
}

const clearSessionAfterRetry = () => {
  if (sessionStorage.reloadRetry) {
    sessionStorage.clear()
  }
  else {
    sessionStorage.reloadRetry = '1'
    clearStoredExpiredTsgTokens()
  }
}

const retryReload = () => {
  clearSessionAfterRetry()
  window.location.reload()
}

const LogoutButton: VFC<{}> = (): ReactElement => {
  const logoutHandler = useCallback(() => {
    logout()
  }, [])
  return <Button
    appearance='clear'
    addClassName='sparky-fullscreen-error-close'
    onClick={logoutHandler}
  >Sign Out</Button>
}

interface FullscreenErrorProps {
  message: string,
  className?: string,
  button?: ReactNode,
  isIPRestricted?: boolean
}
export const FullscreenError: FC<FullscreenErrorProps> = ({ children, message, button, className = 'sparky-fullscreen-error', isIPRestricted = false }): ReactElement => {
  useEffect(clearLoadingMask, [])
  return <div className={classNames(className, PANWDS_TW3_CLS)}>
    {isIPRestricted ? <div className='spf-flex spf-items-end spf-justify-center spf-h-2/4'><InvalidPermissionsSVG width={90} height={90} /></div> : <div className='sparky-logo'></div>}
    <Title size='xxs' level={2}>
      {children}
    </Title>
    <Subtitle>{message}</Subtitle>
    {button || <LogoutButton />}
  </div>
}

export const FullscreenErrorWithMessageInQuery: FC<{}> = ({ children }): ReactElement => {
  const message = useMemo(getMessageParam, [])
  return <FullscreenError message={message}>{children}</FullscreenError>
}

export const NotAuthenticated: VFC<{}> = (): ReactElement => (
  <FullscreenErrorWithMessageInQuery>{_T('Not Authenticated')}</FullscreenErrorWithMessageInQuery>
)

export const NotAuthorized: VFC<{}> = (): ReactElement => (
  <FullscreenErrorWithMessageInQuery>{_T('Not Authorized')}</FullscreenErrorWithMessageInQuery>
)

export const AuthenticatingTimeout: FC<{}> = ({ children = null }): ReactElement => (
  <FullscreenError
    message={_T('Authentication timeout occurred. Please try again. If this issue persists, please contact support.')}
    button={<Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={retryReload}
    >Try Again</Button>}
  >{children}</FullscreenError>
)

export const FailedToRenew: VFC<{}> = (): ReactElement => (
  <FullscreenError
    message={_T('Failed to keep user session due to inactive or network issue, please try reload.')}
    button={<Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={retryReload}
    >Try Again</Button>}
  />
)

const retryHandler = () => {
  const param = new URLSearchParams(window.location.search)
  let path = param.get('path') || '/'
  if (!path.startsWith('/')) {
    path = '/'
  }
  clearSessionAfterRetry()
  window.location.href = path
}
const RetrySessionButton: VFC<{}> = (): ReactElement => {
  return <Button
    appearance='clear'
    addClassName='sparky-fullscreen-error-close'
    onClick={retryHandler}
  >Try Again</Button>
}

export const SessionLost: VFC<{}> = (): ReactElement => {
  return <FullscreenError
    message={_T('Please sign in again.')}
    button={<RetrySessionButton />}
  >{_T('Session Lost')}</FullscreenError>
}

export default FullscreenError
